import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@/context/CartContext';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'About', href: '/about' },
  ];

  const { openCart, totalItems } = useCart();

  const isActive = (href: string) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled ? 'bg-brand-cream/95 backdrop-blur-md border-b border-brand-beige' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://storage.readdy-site.link/project_files/03fe7774-bd67-4ad6-ac29-0dc609d1d53b/78cdd123-162e-4d26-97db-569471a61e50_pearl-logo-2.png?v=7161e591673fd16c8665db95bc0f5e63"
            alt="Pearl Foods Logo"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-sans text-sm font-medium tracking-wide transition-colors duration-200 relative after:absolute after:bottom-0 after:left-0 after:h-0.5 after:bg-brand-gold after:transition-all after:duration-200 ${
                isActive(link.href)
                  ? 'text-brand-olive after:w-full'
                  : `${scrolled ? 'text-brand-brown' : 'text-white'} after:w-0 hover:text-brand-gold hover:after:w-full`
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Cart & WhatsApp */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={openCart}
            className="relative w-10 h-10 flex items-center justify-center rounded-full bg-brand-beige/80 hover:bg-brand-beige text-brand-brown transition-colors cursor-pointer"
            aria-label="Open cart"
          >
            <i className="ri-shopping-basket-2-line text-lg"></i>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center bg-brand-olive text-white text-xs font-sans font-bold rounded-full">
                {totalItems > 9 ? '9+' : totalItems}
              </span>
            )}
          </button>
          <a
            href="https://wa.me/923445299984"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-brand-olive hover:bg-brand-olive-dark text-white font-sans text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base"></i>
            </div>
            Order on WhatsApp
          </a>
        </div>

        {/* Mobile: Cart + Menu */}
        <div className="md:hidden flex items-center gap-2">
          <button
            onClick={openCart}
            className="relative w-9 h-9 flex items-center justify-center rounded-lg text-brand-brown bg-brand-beige/80 cursor-pointer"
            aria-label="Open cart"
          >
            <i className="ri-shopping-basket-2-line text-lg"></i>
            {totalItems > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-brand-olive text-white text-[10px] font-sans font-bold rounded-full">
                {totalItems > 9 ? '9+' : totalItems}
              </span>
            )}
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-brand-brown bg-brand-beige/80 cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-xl`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-brand-cream border-t border-brand-beige px-4 pb-5 pt-3">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`block py-3 font-sans text-sm font-medium border-b border-brand-beige/60 ${
                isActive(link.href) ? 'text-brand-olive' : 'text-brand-brown'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href="https://wa.me/923445299984"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 flex items-center justify-center gap-2 bg-brand-olive text-white font-sans text-sm font-medium px-5 py-3 rounded-full cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-whatsapp-line text-base"></i>
            </div>
            Order on WhatsApp
          </a>
        </div>
      )}
    </header>
  );
};

export default Navbar;
